import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '@/lib/apiClient'
import { useApiClient } from '@/hooks/useApiClient'

export type UsuarioRol = 'administrador' | 'comercial'

export interface UsuarioListItem {
  id: string
  email: string
  rol: UsuarioRol
  ambito: string | null
  sharePointUserId: string | null
  habilitado: boolean
}

export interface CreateUsuarioInput {
  email: string
  rol: UsuarioRol
  ambito?: string | null
  sharePointUserId?: string | null
}

export interface UpdateUsuarioInput {
  rol?: UsuarioRol
  habilitado?: boolean
  ambito?: string | null
  sharePointUserId?: string | null
}

export type UsuariosStatus =
  | 'loading'
  | 'ready'
  | 'not_signed_in'
  | 'not_configured'
  | 'forbidden'
  | 'unauthorized'
  | 'error'

interface UsuariosState {
  status: UsuariosStatus
  usuarios: UsuarioListItem[]
  error: ApiError | Error | null
  reload: () => void
  create: (input: CreateUsuarioInput) => Promise<UsuarioListItem>
  update: (id: string, patch: UpdateUsuarioInput) => Promise<UsuarioListItem>
}

function statusFromError(err: unknown): UsuariosStatus {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'unauthorized'
    if (err.status === 403) return 'forbidden'
    if (err.status === 503) return 'not_configured'
  }
  return 'error'
}

export function useUsuarios(): UsuariosState {
  const { gate, makeClient } = useApiClient()
  const [usuarios, setUsuarios] = useState<UsuarioListItem[]>([])
  const [status, setStatus] = useState<UsuariosStatus>('loading')
  const [error, setError] = useState<ApiError | Error | null>(null)
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(() => setAttempt((v) => v + 1), [])

  useEffect(() => {
    if (gate !== 'ready') {
      setStatus(gate)
      return
    }

    const controller = new AbortController()
    const client = makeClient()
    if (!client) {
      setStatus('not_signed_in')
      return
    }

    setStatus('loading')
    setError(null)

    client
      .get<{ usuarios: UsuarioListItem[] }>('/api/usuarios', { signal: controller.signal })
      .then((response) => {
        setUsuarios(response.usuarios)
        setStatus('ready')
      })
      .catch((err: ApiError | Error) => {
        if (controller.signal.aborted) return
        setUsuarios([])
        setError(err)
        setStatus(statusFromError(err))
      })

    return () => controller.abort()
  }, [gate, attempt, makeClient])

  const create = useCallback(
    async (input: CreateUsuarioInput) => {
      const client = makeClient()
      if (!client) throw new ApiError('API_UNAUTHORIZED', 'Not signed in')
      const response = await client.post<{ usuario: UsuarioListItem }>('/api/usuarios', input)
      reload()
      return response.usuario
    },
    [makeClient, reload]
  )

  const update = useCallback(
    async (id: string, patch: UpdateUsuarioInput) => {
      const client = makeClient()
      if (!client) throw new ApiError('API_UNAUTHORIZED', 'Not signed in')
      const response = await client.patch<{ usuario: UsuarioListItem }>(`/api/usuarios/${id}`, patch)
      reload()
      return response.usuario
    },
    [makeClient, reload]
  )

  return { status, usuarios, error, reload, create, update }
}

export function useAssignableUsuarios() {
  const { gate, makeClient } = useApiClient()
  const [usuarios, setUsuarios] = useState<UsuarioListItem[]>([])
  useEffect(() => {
    if (gate !== 'ready') return
    const client = makeClient(); if (!client) return
    client.get<{ usuarios: UsuarioListItem[] }>('/api/usuarios/asignables').then((response) => setUsuarios(response.usuarios)).catch(() => setUsuarios([]))
  }, [gate, makeClient])
  return usuarios
}
